import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter, Link } from 'react-router-dom';

import { AsideLeft, Footer, Loading, Success, StringHelpers, SystemText } from '@salesperformanceint/e4shared';

import Feedback from 'components/asideRight/components/feedback';
import Header from 'components/header';
import AsideRight from 'components/asideRight';

import { menus } from 'data/menus';

import { loginRedirectFrom, loadUserFromLocalStorage, logout } from 'actions/user';
import { setShowSuccess, toggleLeftAside, toggleLeftAsideMenu } from 'actions/ui';

import { clearError, clearProfile } from 'actions/profile';
import { hasModule } from 'helpers/profile';

import Package from '../../../../package.json';
import config from 'config';

import './main.css';
import '@salesperformanceint/e4shared/lib/shared-bundle.css';
import { getStorage } from 'helpers/storage';

import DebugHelper from 'components/debugHelper';
import { posthog } from 'posthog-js';
import Prism from '../prism';

export const Main = props => {
    const dispatch = useDispatch();

    const [sideStyle, setSideStyle] = React.useState('');
    const [loading, setLoading] = React.useState(true);
    const { user, working } = useSelector(state => state.user);
    const { leftAsideOpen, leftAsideMenusOpen, exploreAsideOpen, filterAsideOpen, rightAsideOpen } = useSelector(
        state => state.ui
    );

    const loadedRef = React.useRef(null);

    const st = (path, ...rest) => SystemText(`General/Footer/${path}`, ...rest);

    const translations = {
        footer: {
            version: st('Version'),
            copyright: st('Copyright'),
            allRights: st('AllRights'),
        },
    };

    React.useEffect(() => {
        if (exploreAsideOpen || filterAsideOpen) {
            setSideStyle('wide');
        }

        if (rightAsideOpen) {
            setSideStyle('');
        }
    }, [exploreAsideOpen, filterAsideOpen, rightAsideOpen]);

    React.useEffect(() => {
        if (document) {
            const style = user?.companySettings?.prism_UI ? 'prism' : 'e4';
            const sidebarOpen = leftAsideOpen ? '' : 'sidebar-open';
            const wideOpen = exploreAsideOpen ? 'wide-open' : '';
            document.body.className = `${style} ${sidebarOpen} ${wideOpen}`;
        }
    }, [leftAsideOpen, document]);

    React.useEffect(() => {
        if ((getStorage('user') || getStorage('tempUser')) && document.TRANSLATIONS?.length) {
            setLoading(false);
        }
    }, [localStorage.user, localStorage.tempUser, document.TRANSLATIONS]);

    React.useEffect(() => {
        if (loadedRef?.current) {
            const body = document.getElementById('e4-body');
            if (user?.companySettings?.prism_UI) {
                body.className = 'prism';
            } else {
                body.className = 'e4';
            }
        }
    }, [loadedRef?.current]);

    React.useEffect(() => {
        const addStyle = url => {
            const head = document.head;
            const links = [...document.querySelectorAll('link[rel="stylesheet"]')];

            const alreadyExists = links.some(link => link.href.includes(url));

            if (!alreadyExists) {
                var link = document.createElement('link');
                link.type = 'text/css';
                link.rel = 'stylesheet';
                link.href = url;
                head.appendChild(link);
            }
        };

        const removeStyle = url => {
            const links = [...document.querySelectorAll('link[rel="stylesheet"]')];
            links.forEach(link => {
                if (link.href.includes(url)) {
                    link.remove();
                }
            });
        };

        if (user?.companySettings?.prism_UI) {
            addStyle('/dist/css/prism.css');
            removeStyle('/dist/css/AdminLTE.min.css');
            removeStyle('/dist/css/skins/_all-skins.min.css');
        } else {
            addStyle('/dist/css/AdminLTE.min.css');
            addStyle('/dist/css/skins/_all-skins.min.css');
            removeStyle('/dist/css/prism.css');
        }
    }, [user?.companySettings?.prism_UI]);

    React.useEffect(() => {
        if (user?.id && !getStorage('user') && !getStorage('tempUser')) {
            dispatch(logout(props.history, user.authority));
        }
    }, [localStorage.user, localStorage.tempUser, user?.id]);

    React.useEffect(() => {
        window?.scrollTo && window.scrollTo(0, 0);

        if (!user) {
            dispatch(loginRedirectFrom(`${window.location.pathname}${window.location.search}`));
            dispatch(loadUserFromLocalStorage(props.history));
        }

        return () => {
            dispatch(setShowSuccess(false));
            dispatch(clearError());
            dispatch(clearProfile());
        };
    }, []);

    if (!user || working) return null;

    const options = { path: '/', secure: true, sameSite: true };
    const lastVersion = getStorage('last-version');

    if (
        StringHelpers.isLaterVersion(user.version, lastVersion) &&
        StringHelpers.isLaterVersion(user.version, Package.version)
    ) {
        cookie.save('last-version', user.version, options);
        window.location.reload();
    }

    const filteredMenus = menus.reduce((all, m) => {
        const top = m.title;
        m = { ...m, trackingName: m.title, title: SystemText(`Menus/${m.title}`) };
        m.children = m.children
            ? m.children
                  .filter(c => !c.module || hasModule(user, c.module))
                  .filter(c => !c.lms || user.lms_Types?.some(t => t === c.lms))
                  .map(c => ({
                      ...c,
                      trackingName: `${m.title}>${c.title}`,
                      title: SystemText(`Menus/${top}/${c.title}`),
                  }))
            : null;
        return !m.module || hasModule(user, m.module) ? all.concat([m]) : all;
    }, []);

    return (
        <div ref={loadedRef}>
            {loading ? (
                <Loading />
            ) : user?.companySettings?.prism_UI ? (
                <div>
                    <Prism history={history} setShowSuccess={setShowSuccess}>
                        {props.children}
                    </Prism>
                </div>
            ) : (
                <div
                    className={`e4-container hold-transition skin-blue sidebar-mini ${
                        leftAsideOpen ? '' : 'sidebar-collapse'
                    } ${user.partner_Id > 0 || user.spoofing ? 'partner-portal' : ''}`}
                >
                    <Feedback />
                    <DebugHelper />
                    <Header />
                    <Success setShowSuccess={setShowSuccess} />
                    <AsideLeft
                        location={location}
                        Link={Link}
                        history={history}
                        menus={filteredMenus}
                        user={user}
                        leftAsideMenusOpen={leftAsideMenusOpen}
                        toggleLeftAsideMenu={id => dispatch(toggleLeftAsideMenu(id))}
                        toggleLeftAside={() => dispatch(toggleLeftAside())}
                        showSearch
                        config={config}
                        trackEvent={trackingName =>
                            //use beacon as we have page navigation so xhr can be cancelled
                            posthog.capture('left-menu-click', { menuItem: trackingName }, { transport: 'sendBeacon' })
                        }
                    />
                    <div id="content" className="content-wrapper">
                        {filterAsideOpen && <div className="content-overlay" />}
                        {exploreAsideOpen && <div className="explore-open" />}
                        {props.children}
                    </div>
                    <Footer version={Package.version} text={translations.footer} />
                    <AsideRight sideStyle={sideStyle} />
                    <div className={`control-sidebar-bg ${sideStyle}`} />
                </div>
            )}
        </div>
    );
};

export default withRouter(Main);
